/** Pulsing ring indicator for active buffs, debuffs, and status effects. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureRingTexture, ensureSoftCircleTexture, CRISP_SCALE } from '../../ui/CrispUtil';

export interface AuraRingConfig extends VfxPrimitiveConfig {
  radius: number;
  lineWidth: number;
  baseAlpha: number;
  pulseAlphaMin: number;
  pulseAlphaMax: number;
  pulsePeriodMs: number;
  expiryFlickerMs: number;
  variant: 'single' | 'double' | 'filled';
  fillTint?: number;
  fillAlpha?: number;
}

export class AuraRing extends VfxPrimitive<AuraRingConfig> {
  private ringKey!: string;
  private fillKey: string | null = null;
  private outerRingKey: string | null = null;
  private ringImg!: Phaser.GameObjects.Image;
  private fillImg: Phaser.GameObjects.Image | null = null;
  private outerRingImg: Phaser.GameObjects.Image | null = null;
  private pulseTween: Phaser.Tweens.Tween | null = null;
  private outerPulseTween: Phaser.Tweens.Tween | null = null;
  private playing = false;
  private remainingMs = Infinity;

  protected allocatePool(): void {
    const { config } = this;
    const crispRadius = config.radius * CRISP_SCALE;
    const crispLineWidth = config.lineWidth * CRISP_SCALE;

    // Primary ring
    this.ringKey = ensureRingTexture(this.scene, crispRadius, crispLineWidth);
    this.ringImg = this.createPoolImage(this.ringKey);

    // Filled variant: soft circle behind ring
    if (config.variant === 'filled') {
      this.fillKey = ensureSoftCircleTexture(this.scene, crispRadius);
      this.fillImg = this.createPoolImage(this.fillKey);
      if (config.fillTint != null) this.fillImg.setTint(config.fillTint);
    }

    // Double variant: outer ring at 1.4× radius
    if (config.variant === 'double') {
      const outerRadius = Math.round(crispRadius * 1.4);
      this.outerRingKey = ensureRingTexture(this.scene, outerRadius, crispLineWidth);
      this.outerRingImg = this.createPoolImage(this.outerRingKey);
    }
  }

  play(x: number, y: number, depth: number): void {
    this.playing = true;
    this.remainingMs = Infinity;

    this.activate(this.ringImg, x, y, depth);
    this.ringImg.setAlpha(this.config.baseAlpha);

    if (this.fillImg) {
      this.activate(this.fillImg, x, y, depth);
      this.fillImg.setAlpha(this.config.fillAlpha ?? 0.2);
      this.fillImg.setDepth(depth + (this.config.depthOffset ?? 0) - 0.1);
    }
    if (this.outerRingImg) {
      this.activate(this.outerRingImg, x, y, depth);
      this.outerRingImg.setAlpha(this.config.baseAlpha * 0.6);
    }

    this.startPulse(this.config.pulsePeriodMs);
  }

  stop(): void {
    this.playing = false;
    this.killPulseTweens();

    const targets = [this.ringImg, this.fillImg, this.outerRingImg].filter(
      (img): img is Phaser.GameObjects.Image => img != null && img.active,
    );

    if (targets.length === 0) return;

    this.scene.tweens.add({
      targets,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        for (const img of targets) this.deactivate(img);
      },
    });
  }

  /** Reposition ring to follow a moving entity. Call every frame while active. */
  updatePosition(x: number, y: number, depth: number, _delta: number): void {
    if (!this.playing) return;

    const d = depth + (this.config.depthOffset ?? 0);
    this.ringImg.setPosition(x, y).setDepth(d);
    if (this.fillImg?.active) this.fillImg.setPosition(x, y).setDepth(d - 0.1);
    if (this.outerRingImg?.active) this.outerRingImg.setPosition(x, y).setDepth(d);
  }

  /** Set remaining duration — triggers flicker when below expiryFlickerMs. */
  setRemainingMs(ms: number): void {
    const wasAboveThreshold = this.remainingMs >= this.config.expiryFlickerMs;
    this.remainingMs = ms;

    if (ms < this.config.expiryFlickerMs && wasAboveThreshold && this.playing) {
      this.killPulseTweens();
      this.startPulse(this.config.pulsePeriodMs / 2);
    }
  }

  private startPulse(periodMs: number): void {
    this.killPulseTweens();
    const { pulseAlphaMin, pulseAlphaMax } = this.config;

    this.pulseTween = this.scene.tweens.add({
      targets: this.ringImg,
      alpha: { from: pulseAlphaMin, to: pulseAlphaMax },
      duration: periodMs / 2,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    if (this.outerRingImg?.active) {
      this.outerPulseTween = this.scene.tweens.add({
        targets: this.outerRingImg,
        alpha: { from: pulseAlphaMin * 0.6, to: pulseAlphaMax * 0.6 },
        duration: periodMs / 2,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private killPulseTweens(): void {
    if (this.pulseTween) {
      this.pulseTween.destroy();
      this.pulseTween = null;
    }
    if (this.outerPulseTween) {
      this.outerPulseTween.destroy();
      this.outerPulseTween = null;
    }
  }
}
